"use client";

import { type FC, type Ref, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ExpandableCard,
  type ExpandableCardProps,
  usePubSub,
  type UIAction,
  type UINavigateAction,
  useUuid,
  useActions,
  Tooltip,
  Flex,
} from "@huub-web/ui";
import { isString } from "@huub-web/utils";
import { useTemplates } from "../../hooks/useTemplates";
import type { TemplateVariableType } from "../../types/TemplateVariableType";

import { TemplatesParser, TemplateVariableValue } from "../../components";
import { useAsyncTemplates } from "../../hooks/useAsyncTemplates";
import { usePageContent } from "../../hooks/usePageContent";

type ModelEntryCardTemplatePropsType = Pick<
  ExpandableCardProps,
  "disabled" | "open" | "buttonValue" | "warning"
> & {
  /**
   * Optional ID override
   */
  id?: string;
  /**
   * Card title
   */
  title?: TemplateVariableType | string;
  /**
   * Card subtitle
   */
  subtitle?: TemplateVariableType | string;
  /**
   * Whether the card is in active state
   */
  active?: boolean;
  /**
   * Whether the card is completed
   */
  completed?: boolean;
  /**
   * API endpoint for data loading
   */
  endpoint?: string;
  /**
   * Routing context array
   */
  context?: string[];
  /**
   * Action to execute when the card is opened (e.g. navigate)
   */
  action?: UIAction;
  /**
   * Custom tooltip text shown when the card is disabled.
   * Falls back to the default "fill in personal details" message.
   */
  disabledReason?: string;
};

export interface ModelEntryCardTemplateProps
  extends ModelEntryCardTemplatePropsType {}

/**
 * A template for a model entry card that loads and displays data models
 * for specific subjects based on routing.
 */
export const ModelEntryCardTemplate: FC<
  ModelEntryCardTemplateProps & { ref?: Ref<HTMLDivElement> }
> = ({
  id: idProp,
  title,
  subtitle,
  buttonValue,
  open,
  active,
  disabled,
  completed,
  endpoint,
  context,
  action,
  warning,
  disabledReason,
  ref,
}) => {
  const { t } = useTranslation();
  const id = useUuid(idProp);
  const pubSub = usePubSub();
  const { handleAction } = useActions();
  const { lockUI } = useTemplates();

  const { templatesData, loading, hasLoadedOnce, error, clear } =
    useAsyncTemplates(endpoint, id, open);

  const resolvedContext =
    context ??
    (action?.type === "navigate"
      ? (action as UINavigateAction).payload?.context
      : undefined);

  const titleStr = isString(title) ? title : undefined;
  const contentItem = useMemo(
    () =>
      titleStr
        ? {
            type: "card" as const,
            label: titleStr,
            meta: {
              ...(completed ? { voltooid: true } : {}),
              ...(disabled ? { uitgeschakeld: true } : {}),
            },
            ...(action
              ? { action }
              : resolvedContext
                ? {
                    action: {
                      type: "navigate" as const,
                      payload: { context: resolvedContext },
                    },
                  }
                : {}),
          }
        : null,
    [titleStr, completed, disabled, action, resolvedContext],
  );
  const contentKey = titleStr ? `model-entry-${titleStr}` : "model-entry";
  usePageContent(contentKey, contentItem);

  useEffect(() => {
    if (!open) {
      clear();
    }
  }, [open]);

  const handleOpen = () => {
    if (action) {
      handleAction(action);
    } else if (resolvedContext) {
      handleAction({
        type: "navigate",
        payload: { context: resolvedContext },
      });
    }
    setTimeout(() => {
      pubSub.publish("scrollToTop", { tag: "carousel" });
    }, 350);
  };

  /**
   * True when there's somewhere for `handleOpen` to go: an expandable
   * endpoint, an explicit action, or a navigate context. Passing
   * `onOpen` to `<ExpandableCard>` is what makes the card visually
   * interactive (cursor pointer, hover border, click-through). A
   * completed card with only `completed: true` and a subtitle — no
   * endpoint, action, or context — must render as a static tile, so
   * we withhold `onOpen` in that case.
   */
  const canOpen = !!endpoint || !!action || !!resolvedContext;

  const handleClose = () => {
    if (resolvedContext) {
      handleAction({
        type: "navigate",
        payload: { context: resolvedContext.slice(0, -1) },
      });
    } else {
      pubSub.publish("refreshTemplates", {});
    }
  };

  return (
    <Tooltip
      value={disabled && (disabledReason || t("modelEntryCard.lockedTooltip"))}
      placement="center"
    >
      <ExpandableCard
        id={id}
        ref={ref}
        buttonValue={buttonValue}
        active={active}
        disabled={disabled || lockUI}
        warning={open ? undefined : warning}
        open={!!endpoint && open}
        completed={open ? undefined : completed}
        onOpen={canOpen ? handleOpen : undefined}
        onClose={handleClose}
        title={title && <TemplateVariableValue value={title} />}
        subtitle={subtitle && <TemplateVariableValue value={subtitle} />}
        label={isString(title) ? title : undefined}
        expandable={!!endpoint}
        loading={loading || (lockUI && open)}
        hasContent={hasLoadedOnce}
        error={error}
        content={
          templatesData ? (
            <Flex gap="s" direction="column">
              <TemplatesParser level={2} templates={templatesData} />
            </Flex>
          ) : undefined
        }
      />
    </Tooltip>
  );
};
